import {
  getNominatimAcceptLanguage,
  MapProvider,
} from "../../../utils/mapProvider";

export type NearbyPlace = {
  id: string;
  name: string;
  label: string;
  lat: number;
  lng: number;
};

type NominatimItem = {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  name?: string;
  address?: {
    road?: string;
    suburb?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
  };
};

function placeLabel(item: NominatimItem) {
  if (item.name) return item.name;
  const parts = item.display_name.split(",");
  return parts[0]?.trim() || item.display_name;
}

function placeSubtitle(item: NominatimItem) {
  const parts = item.display_name.split(",").map((part) => part.trim());
  return parts.slice(1, 3).join(", ");
}

async function nominatimFetch(url: string, provider: MapProvider) {
  const response = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Accept-Language": getNominatimAcceptLanguage(provider),
    },
  });
  if (!response.ok) {
    throw new Error("Geocoder request failed");
  }
  return response.json();
}

export async function reverseGeocode(
  lat: number,
  lng: number,
  provider: MapProvider,
): Promise<string> {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;
  const data = (await nominatimFetch(url, provider)) as NominatimItem;
  return data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

export async function searchPlaces(
  query: string,
  provider: MapProvider,
  near?: { lat: number; lng: number },
): Promise<NearbyPlace[]> {
  const q = query.trim();
  if (!q) return [];

  const params = new URLSearchParams({
    format: "jsonv2",
    q,
    addressdetails: "1",
    limit: "8",
  });

  if (near) {
    const delta = 0.2;
    params.set(
      "viewbox",
      `${near.lng - delta},${near.lat + delta},${near.lng + delta},${near.lat - delta}`,
    );
    params.set("bounded", "0");
  }

  const data = (await nominatimFetch(
    `https://nominatim.openstreetmap.org/search?${params.toString()}`,
    provider,
  )) as NominatimItem[];

  return data.map((item) => ({
    id: String(item.place_id),
    name: placeLabel(item),
    label: placeSubtitle(item) || item.display_name,
    lat: Number(item.lat),
    lng: Number(item.lon),
  }));
}

export async function fetchNearbyPlaces(
  lat: number,
  lng: number,
  provider: MapProvider,
): Promise<NearbyPlace[]> {
  const params = new URLSearchParams({
    format: "jsonv2",
    q: provider === "yandex" ? "место" : "place",
    addressdetails: "1",
    limit: "12",
  });
  const delta = 0.03;
  params.set(
    "viewbox",
    `${lng - delta},${lat + delta},${lng + delta},${lat - delta}`,
  );
  params.set("bounded", "1");

  try {
    const data = (await nominatimFetch(
      `https://nominatim.openstreetmap.org/search?${params.toString()}`,
      provider,
    )) as NominatimItem[];

    if (data.length > 0) {
      return data.map((item) => ({
        id: String(item.place_id),
        name: placeLabel(item),
        label: placeSubtitle(item) || item.display_name,
        lat: Number(item.lat),
        lng: Number(item.lon),
      }));
    }
  } catch {
    // fall through to reverse-only result
  }

  const address = await reverseGeocode(lat, lng, provider);
  return [
    {
      id: `current-${lat}-${lng}`,
      name: address.split(",")[0]?.trim() || address,
      label: address,
      lat,
      lng,
    },
  ];
}
